/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext } from 'react';
import { Grid, Table, TableBody, TableCell, TableHead, TableRow, TextField, Checkbox, Select, MenuItem, Button, FormControl, TableSortLabel, Autocomplete, TableContainer } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { theme } from '../../../theme';
import { UserAdjustmentDetails } from '../types/UserAdjustmentDetails';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { useEffect, useState } from 'react';
import { Persona, Store } from '../../../app/services/api/generated';
import { UserContext } from '../../../components/shared/useUser';
import LoadingWheel from '../../../components/ui/LoadingWheel';



interface BulkUserReviewTableProps {
  users: UserAdjustmentDetails[];
  onChange: (users: UserAdjustmentDetails[]) => void;
  personaData: Persona[];
}



const BulkUserReviewTable: React.FC<BulkUserReviewTableProps> = ({ users, onChange, personaData }) => {
  const { t } = useTranslation('manageUsers');
  const [search, setSearch] = React.useState('');
  const [order, setOrder] = React.useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = React.useState<string>('email');
  const [stores, setStores] = useState<Store[]>([]);
  const { getFranchiseStores } = useNswagClient();
  const { selectedFranchise } = useContext(UserContext);

  const personas = personaData;

  const franchise = selectedFranchise?.franchiseName;
  useEffect(() => {
    if (!franchise) {
      setStores([]);
      return;
    }
    getFranchiseStores(franchise).then(r => {
      if (r?.data?.stores) setStores(r.data.stores);
      else setStores([]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [franchise]);

  const handleFieldChange = (userId: number, field: keyof UserAdjustmentDetails | Record<string, any>, value?: any) => {
    const userIdx = users.findIndex(u => u.id === userId);
    const updated = [...users];
    if (typeof field === 'object') {
      updated[userIdx] = { ...updated[userIdx], ...field };
    } else {
      updated[userIdx] = { ...updated[userIdx], [field]: value };
    }
    onChange(updated);
  };

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedUsers = React.useMemo(() => {
    const hasValidationError = users.some(u =>
      !u.email ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(u.email) ||
      users.filter(x => x.email?.trim().toLowerCase() === u.email?.trim().toLowerCase()).length > 1,
    );
    if (hasValidationError) {
      // Return users in their original order to prevent jumping rows
      return [...users];
    }
    const arr = [...users];
    arr.sort((a, b) => {
      let aValue: any = a[orderBy as keyof UserAdjustmentDetails];
      let bValue: any = b[orderBy as keyof UserAdjustmentDetails];

      // Custom sort for persona/role
      if (orderBy === 'role') {
        aValue = a.role ?? '';
        bValue = b.role ?? '';
        return order === 'asc'
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      }

      // Custom sort for stores (by count)
      if (orderBy === 'stores') {
        aValue = Array.isArray(a.stores) ? a.stores.length : 0;
        bValue = Array.isArray(b.stores) ? b.stores.length : 0;
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Default string sort
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return order === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      // Boolean sort
      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        return order === 'asc'
          ? (aValue === bValue ? 0 : aValue ? -1 : 1)
          : (aValue === bValue ? 0 : aValue ? 1 : -1);
      }
      // Number sort
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }
      return 0;
    });
    return arr;
  }, [users, order, orderBy]);

  const filteredUsers = search.trim() === ''
    ? sortedUsers
    : sortedUsers.filter(u => u.email?.toLowerCase().includes(search.trim().toLowerCase()));

  const storesLoaded = franchise === undefined || stores.some(s => franchise === s.franchiseName);

  if (!storesLoaded) {
    return (
      <LoadingWheel />
    );
  }

  return (
    <Grid container
      sx={{ border: '1px solid', borderColor: theme.palette.custom.gray[300], py: '12px', px: '8px', mt: 3 }}
      spacing={3}>
      <Grid item
        md={3}
        xs={12}>
        <FormControl fullWidth
          size='small'>
          <TextField
            id="user-search-filter"
            label={t('searchByUser')}
            size='small'
            variant="outlined"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </FormControl>
      </Grid>
      <Grid item
        xs={12}
        md={7}></Grid>
      <Grid item
        xs={12}
        sx={{ mt: '20px' }} >
        <TableContainer>
          <Table
            size="small"
            aria-label="bulk user review table"
            sx={{ minWidth: 900 }}
          >
            <TableHead sx={{ paddingTop: '10px', backgroundColor: theme.palette.custom.gray[200] }}>
              <TableRow>
                <TableCell align="left"
                  width={'25%'}>
                  <TableSortLabel
                    active={orderBy === 'email'}
                    direction={order}
                    onClick={() => handleSort('email')}
                  >
                    {t('email')}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="left"
                  width={'10%'}>
                  {t('passwordReset')}
                </TableCell>
                <TableCell align="left"
                  width={'10%'}>
                  {t('deleteUser')}
                </TableCell>
                <TableCell align="left"
                  width={'10%'}>
                  <TableSortLabel
                    active={orderBy === 'franchise'}
                    direction={order}
                    onClick={() => handleSort('franchise')}
                  >
                    {t('franchise')}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="left"
                  width={'10%'}>
                  <TableSortLabel
                    active={orderBy === 'role'}
                    direction={order}
                    onClick={() => handleSort('role')}
                  >
                    {t('role', 'Role')}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="left"
                  width={'10%'}>
                  <TableSortLabel
                    active={orderBy === 'stores'}
                    direction={order}
                    onClick={() => handleSort('stores')}
                  >
                    {t('stores')}
                  </TableSortLabel>
                </TableCell>

                <TableCell align="left"
                  width={'10%'}>
                  {t('allStores')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user, idx) => {
                const getBool = (val: any) => val === true || val === 'true' || val === 'TRUE' || val === 1 || val === '1';
                const personaOptions = personas.filter(p => p.franchiseName === user.franchise);
                const storeOptions = stores;

                const isDuplicateEmail =
                  user.email &&
                  users.some(
                    (u, i) =>
                      u.id !== user.id &&
                      u.email &&
                      u.email.trim().toLowerCase() === user.email.trim().toLowerCase(),
                  );

                return (
                  <TableRow key={user.id}>
                    <TableCell align="left" >
                      <TextField
                        value={user.email}
                        onChange={e => {if (user.id) {handleFieldChange(user.id , 'email', e.target.value);}}}
                        type="email"
                        required
                        sx={{ minWidth: 300 }}
                        error={
                          !!(
                            !user.email ||
                            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.email) ||
                            isDuplicateEmail
                          )
                        }
                        helperText={
                          !user.email
                            ? t('emailRequired')
                            : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.email)
                              ? t('invalidEmail')
                              : isDuplicateEmail
                                ? t('duplicateEmail')
                                : ''
                        }
                      />
                    </TableCell>
                    <TableCell align="left">
                      <Checkbox
                        checked={getBool(user.passwordReset)}
                        onChange={e => {if (user.id ) {handleFieldChange(user.id, 'passwordReset', e.target.checked);}}}
                      />
                    </TableCell>
                    <TableCell align="left">
                      <Checkbox
                        checked={getBool(user.deleteUser)}
                        onChange={e => {if (user.id) { handleFieldChange(user.id, 'deleteUser', e.target.checked);}}}
                      />
                    </TableCell>
                    <TableCell align="left">
                      {user.franchise}
                    </TableCell>
                    <TableCell align="left" >
                      <Select
                        value={user.role}
                        onChange={e => {if (user.id !== undefined) {handleFieldChange(user.id, 'role', e.target.value);}}}
                        sx={{ minWidth: 140, maxWidth: 220, width: 160, textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxWidth: 300,
                            },
                          },
                        }}
                      >
                        {personaOptions.map(role => (
                          <MenuItem key={role.id}
                            value={role.description}
                            style={{ maxWidth: 280, whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset' }}>{role.description}</MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell align="left" >
                      <Autocomplete
                        multiple
                        options={storeOptions}
                        getOptionLabel={option => option.storeName || ''}
                        value={user.allStores ? [] : storeOptions.filter(option => (user.stores || []).includes(option.storeNumber ?? ''))}
                        onChange={(_event, newValue) => {
                          if(user.id){
                            if (newValue.length === storeOptions.length && storeOptions.length > 0) {
                              handleFieldChange(user.id, { allStores: true, stores: [] });
                            } else {
                              handleFieldChange(user.id, { stores: newValue.map(s => s.storeNumber), allStores: false });
                            }}
                        }}
                        isOptionEqualToValue={(option, value) => option.storeNumber === value.storeNumber}
                        renderInput={params => (
                          <TextField {...params}
                            variant="outlined"
                            size="small"
                            placeholder={t('selectStores', 'Select stores')}
                            sx={{ minWidth: 140, maxWidth: 220, width: 160 }} />
                        )}
                        sx={{ minWidth: 140, maxWidth: 220, width: 160 }}
                        disabled={!!user.allStores}
                      />
                    </TableCell>
                    <TableCell align="left">
                      <Checkbox
                        checked={getBool(user.allStores)}
                        onChange={e => {
                          const checked = e.target.checked;
                          if(user.id)
                            handleFieldChange(user.id, { allStores: checked, stores: checked ? [] : user.stores });
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
};

export default BulkUserReviewTable;
